import React, { useMemo } from "react";
import { Outlet, useMatches } from "react-router-dom";
import { PermissionsError } from "@/components/PermissionsError";
import {
  FEATURE_PROTECTED_ROUTES,
  PARTNERSHIP_PROTECTED_ROUTES,
} from "@/routes/protectedRoutes";
import { type RouteId } from "@/routes/getRoute";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface RouteHandle {
  routeId?: RouteId;
  [key: string]: unknown;
}

interface RouteData {
  routeId?: RouteId;
  [key: string]: unknown;
}

interface ExtendedRouteMatch {
  pathname: string;
  handle?: RouteHandle;
  data?: RouteData;
  id: string;
  params: Record<string, string>;
  route?: {
    routeId?: RouteId;
    [key: string]: unknown;
  };
}

/**
 * Enhanced Outlet that automatically checks permissions based on the current route's routeId
 * This replaces the standard <Outlet /> in DashboardLayout
 */
export const ProtectedOutlet = () => {
  const matches = useMatches();

  const { hasOrgFeature, hasMinOrgPartnershipLevel } = useOrgPermissions();

  const currentRouteId = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i] as ExtendedRouteMatch;

      const routeId =
        match.handle?.routeId || match.data?.routeId || match.route?.routeId;

      if (routeId) {
        return routeId as RouteId;
      }
    }
    return null;
  }, [matches]);

  if (!currentRouteId) {
    return <Outlet />;
  }

  const partnerFeature = FEATURE_PROTECTED_ROUTES[currentRouteId];
  const requiredPartnershipLevel = PARTNERSHIP_PROTECTED_ROUTES[currentRouteId];

  if (partnerFeature) {
    const hasFeature = hasOrgFeature(partnerFeature);

    if (hasFeature === null) {
      return <PermissionsError type="permission" feature={null} />;
    }

    if (!hasFeature) {
      return <PermissionsError type="feature" feature={partnerFeature} />;
    }
  }

  if (requiredPartnershipLevel) {
    const hasLevel = hasMinOrgPartnershipLevel(requiredPartnershipLevel);

    if (hasLevel === null) {
      return <PermissionsError type="permission" feature={null} />;
    }

    if (!hasLevel) {
      return <PermissionsError type="permission" feature={null} />;
    }
  }

  return <Outlet />;
};
